import { ref, watch } from "vue";
import { useRouter } from "vue-router";
import { storeToRefs } from "pinia";
import { FilterContext, useFiltersStore } from "@/_store/filters.module";
import isEqual from "lodash/isEqual";
import defaultsDeep from "lodash/defaultsDeep";
import type { Pagination } from "@/types";
import { useMspStore } from "@/_store/msp/msp.module.ts";
import type { useGlobalWorkspacesStore } from "@/_store/global-workspaces.module.ts";
import { useWorkspaceStore } from "@/_store/workspace.module.ts";
import { ManageWorkspacesContext } from "@/constants/msp.ts";
import { useFilters } from "@/composables/useFilters.ts";

/** Maps a ManageWorkspacesContext to its corresponding FilterContext for type-safe filter access. */
type ContextToFilterContext<T extends ManageWorkspacesContext> =
  T extends ManageWorkspacesContext.GLOBAL_WORKSPACES
    ? FilterContext.GLOBAL_WORKSPACES
    : FilterContext.MSP;

/**
 * Shared composable for the list-based workspace management pages (MSP list tab and Global workspaces).
 * Handles server-side filtered pagination, filter initialization from URL params, and cleanup.
 * Not used by the treeview tab, which filters client-side via `filterWorkspaceTreeByFilters`.
 */
export function useWorkspaceManagement<T extends ManageWorkspacesContext>(
  workspaceStore: ReturnType<typeof useMspStore | typeof useGlobalWorkspacesStore>,
  context: T
) {
  const filterContext =
    context === ManageWorkspacesContext.GLOBAL_WORKSPACES
      ? FilterContext.GLOBAL_WORKSPACES
      : FilterContext.MSP;

  // Type assertion to ensure filterContext is correctly typed based on context
  const typedFilterContext = filterContext as ContextToFilterContext<T>;

  const filtersChangeTimestamp = ref(0);
  const filtersStore = useFiltersStore();
  const baseWorkspaceStore = useWorkspaceStore();
  const router = useRouter();

  const { showSkeletonLoader, loading, workspaces, total, pagination } =
    storeToRefs(workspaceStore);
  const { workspaceAndPermissionsUpdatedTimestamp } = storeToRefs(baseWorkspaceStore);

  const { localFilters, showClearFiltersButton, clearFilters } = useFilters({
    context: typedFilterContext,
    searchKey: "search",
    onFilterChange: async () => {
      await workspaceStore.getMspWorkspaces({ triggeredByFilters: true });
      filtersChangeTimestamp.value = new Date().getTime();
    },
  });

  watch(workspaceAndPermissionsUpdatedTimestamp, async () => {
    workspaceStore.resetPagination();
    await workspaceStore.getMspWorkspaces();
  });

  const onPageChange = async (pageObj: Pagination) => {
    if (isEqual(pageObj, pagination.value)) return;
    workspaceStore.setPagination(pageObj);
    await workspaceStore.getMspWorkspaces();
  };

  const clearFiltersCallback = async () => {
    workspaceStore.resetPagination();
    await workspaceStore.getMspWorkspaces();
  };

  const initializeFilters = async (searchParam?: string) => {
    const filtersFromQuery = {
      search: searchParam,
    };

    localFilters.value = defaultsDeep(filtersFromQuery, localFilters.value);
    await workspaceStore.getMspWorkspaces({ showSkeletonLoader: true });
    await router.replace({});
  };

  const cleanupFilters = () => {
    filtersStore.resetFilters(filterContext);
  };

  return {
    filtersChangeTimestamp,
    showSkeletonLoader,
    loading,
    workspaces,
    total,
    pagination,
    localFilters,
    showClearFiltersButton,
    clearFilters,
    clearFiltersCallback,
    onPageChange,
    initializeFilters,
    cleanupFilters,
  };
}
